/**
 * Sticky cart bar — 2 Sep 2026
 *
 * Shows a persistent route to the bag once there is something in it and the
 * shopper has scrolled down. See snippets/sticky-cart-bar.liquid for why the
 * store needs this (non-sticky header, dot-only cart indicator).
 *
 * Two rules it never breaks:
 *
 *   1. It does not open the drawer itself. The button carries the theme's own
 *      data-action="open-drawer", so the theme's delegated handler runs and the
 *      focus trap and scroll lock behave exactly as they do from the header.
 *
 *   2. It does not format money. The total is mirrored from the drawer's
 *      checkout button, which Liquid renders with money_with_currency. Doing the
 *      formatting here would print dollars at a shopper who had switched the
 *      picker to GBP.
 */
(function () {
  'use strict';

  var bar = document.querySelector('[data-sticky-cart]');
  if (!bar) return;

  var countEl = bar.querySelector('[data-sticky-cart-count]');
  var totalEl = bar.querySelector('[data-sticky-cart-total]');

  // Show only after the shopper has committed to the page. Below this she is
  // still at the top, where the header cart is on screen anyway.
  var SCROLL_TRIGGER = 250;

  var hasItems = !bar.hasAttribute('data-cart-empty');
  var scrolled = false;

  // === KEEP A CHAT BUBBLE CLEAR OF THE BAR ===
  // Only on pages where sticky-atc.js is NOT running. That script does the same
  // job, and two scripts independently saving and restoring the same element's
  // `bottom` is how you end up permanently restoring the OTHER one's modified
  // value as if it were the original. One owner per page, never two.
  var atcOwnsWidgets = !!document.querySelector('[data-sticky-atc]');
  var lifted = [];

  function liftWidgets() {
    if (atcOwnsWidgets || lifted.length) return;
    if (window.innerWidth >= 1008) return;
    var offset = (bar.offsetHeight || 52) + 14;
    var children = document.body.children;
    for (var i = 0; i < children.length; i++) {
      var el = children[i];
      if (el === bar || el.tagName === 'SCRIPT' || el.tagName === 'STYLE') continue;
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed') continue;
      var b = parseFloat(cs.bottom);
      if (isNaN(b) || b > 140) continue;
      var rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) continue;
      if (rect.width > window.innerWidth * 0.9) continue;   // an overlay, not a bubble
      lifted.push({ el: el, original: el.style.bottom });
      el.style.setProperty('bottom', offset + 'px', 'important');
    }
  }

  function dropWidgets() {
    lifted.forEach(function (item) {
      if (item.original) item.el.style.setProperty('bottom', item.original);
      else item.el.style.removeProperty('bottom');
    });
    lifted = [];
  }

  function update() {
    var on = hasItems && scrolled;
    bar.classList.toggle('is-visible', on);
    document.body.classList.toggle('StickyCart--visible', on);
    if (on) {
      liftWidgets();
      // Chat apps mount late; re-check once they have had time to appear.
      setTimeout(function () {
        if (bar.classList.contains('is-visible')) { dropWidgets(); liftWidgets(); }
      }, 1200);
    } else {
      dropWidgets();
    }
  }

  // === MIRROR THE DRAWER'S OWN TOTAL ===
  // The drawer re-renders on every cart change, and its checkout button carries
  // the Liquid-formatted total. Reading it is how this bar stays currency-correct.
  function drawerTotal() {
    var btn = document.querySelector('#sidebar-cart .Cart__Checkout');
    if (!btn) return null;
    var spans = btn.querySelectorAll('span');
    if (!spans.length) return null;
    var text = spans[spans.length - 1].textContent.trim();
    return text || null;
  }

  function paintTotal() {
    var t = drawerTotal();
    if (t && totalEl) totalEl.textContent = t;
  }

  function refresh() {
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        hasItems = cart.item_count > 0;
        if (countEl) {
          countEl.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
        }
        paintTotal();
        update();
      })
      .catch(function () { /* leave the bar as it is rather than blanking it */ });
  }

  // The theme re-renders the drawer asynchronously after an add, so the total is
  // not ready the instant the event fires. Watching the drawer catches it
  // whenever it lands, instead of guessing at a delay.
  var drawer = document.getElementById('sidebar-cart');
  if (drawer && 'MutationObserver' in window) {
    new MutationObserver(paintTotal).observe(drawer, { childList: true, subtree: true });
  }

  document.addEventListener('product:added', refresh);
  document.addEventListener('cart:updated', refresh);
  document.documentElement.addEventListener('cart:refresh', refresh);

  window.addEventListener('scroll', function () {
    var now = window.pageYOffset > SCROLL_TRIGGER;
    if (now !== scrolled) { scrolled = now; update(); }
  }, { passive: true });

  // A reload lands mid-page on a back-navigation, so evaluate once on boot
  // rather than waiting for the first scroll event.
  scrolled = window.pageYOffset > SCROLL_TRIGGER;
  paintTotal();
  update();

  // Then ask the cart what is actually in it. The Liquid render above is right
  // almost always, but it is a snapshot of the moment the page was built — it
  // goes stale if the page came from cache, if the shopper added something in
  // another tab, or if she arrived here on a back-navigation from checkout.
  // One small request buys certainty that the bar is never wrong about the bag.
  refresh();
})();
